import {
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined,
} from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import MuiTypography from '@mui/material/Typography';
import { format } from 'date-fns';
import type { FC } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import InfoCard from 'components/InfoCard/InfoCard';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { StorageE } from 'declarations/enums/storage';
import type { BannerMessageI } from 'declarations/interfaces/notifications';
import CardLoader from 'pages/TicketsPage/components/CardLoader/CardLoader';
import { CardLoaderWrapStyled } from 'pages/TicketsPage/components/CardLoader/CardLoaderWrapStyled';
import { HorizontallyCollapsibleCard } from 'pages/TicketsPage/components/HorizontallyCollapsibleCard/HorizontallyCollapsibleCard';

import {
  BannerCardWrapStyled,
  BannerContentWrapStyled,
  MessageBubbleStyled,
  MessagesStackStyled,
} from './BannerMessages.styled';
import useBannerMessages from './hooks/useBannerMessages';

type LinkifyPropsT = {
  messageText: string;
  linkClass?: string;
};

const Linkify: FC<LinkifyPropsT> = ({ messageText, linkClass }): JSX.Element => {
  const isUrl = (word: string): boolean => {
    // eslint-disable-next-line no-useless-escape
    return /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/gm.test(
      word
    );
  };

  const addMarkup = (word: string, index: number): JSX.Element => {
    const space = index === 0 ? '' : ' ';
    const url = isUrl(word) ? word : null;

    if (url) {
      return (
        <React.Fragment key={index}>
          {space}
          <a href={url} target="_blank" rel="noreferrer" className={linkClass}>
            {word}
          </a>
          {space}
        </React.Fragment>
      );
    }
    return (
      <React.Fragment key={index}>
        {space}
        {word}
        {space}
      </React.Fragment>
    );
  };

  const words = messageText.split(' ');
  const formattedWords = words.map((w, i): JSX.Element => addMarkup(w, i));

  return <span>{formattedWords}</span>;
};

function formatBannerDateTime(message: BannerMessageI): string {
  const date = message.updatedDateTime ?? message.createdDateTime;
  return format(new Date(date), 'MMMM d, h:mm a');
}

const CardRow = (): JSX.Element => {
  const [, setReRender] = useState(false);
  const messagesStackRef = useRef<HTMLDivElement>(null);
  const [isBannerExpanded, setIsBannerExpanded] = useState(false);

  const { bannerMessages, isLoadingBannerMessages, isFetchingBannerMessages } = useBannerMessages();
  const { isMobile } = useResponsive();

  useEffect((): void => {
    if (bannerMessages !== null) {
      bannerMessages?.forEach((message): void => {
        const updatedDateTime = message.updatedDateTime?.toString() ?? new Date(0).toISOString();
        const previousTime = localStorage.getItem(message.id) ?? new Date(0).toISOString();

        if (Date.parse(previousTime) === Date.parse(updatedDateTime)) {
          message.isNew = false;
        } else {
          message.isNew = true;
        }

        localStorage.setItem(message.id, updatedDateTime);
      });
    }

    setReRender((prev): boolean => !prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerMessages]);

  const sortedMessages = useMemo((): BannerMessageI[] => {
    if (!bannerMessages?.length) return [];
    return [...bannerMessages].sort((a, b): number => {
      const dateA = new Date(a.updatedDateTime ?? a.createdDateTime).getTime();
      const dateB = new Date(b.updatedDateTime ?? b.createdDateTime).getTime();
      return dateA - dateB;
    });
  }, [bannerMessages]);

  const hasMessages = sortedMessages.length > 0;
  const isLoading = isLoadingBannerMessages || isFetchingBannerMessages;

  useEffect((): void => {
    if (hasMessages && messagesStackRef.current) {
      messagesStackRef.current.scrollTop = messagesStackRef.current.scrollHeight;
    }
  }, [hasMessages, sortedMessages]);

  const bannerInnerContent = (
    <InfoCard fullWidth={false} compact>
      <BannerContentWrapStyled>
        {isLoading ? (
          <CardLoaderWrapStyled>
            <CardLoader />
          </CardLoaderWrapStyled>
        ) : (
          <MessagesStackStyled ref={messagesStackRef}>
            {hasMessages ? (
              sortedMessages.map(
                (message): JSX.Element => (
                  <MessageBubbleStyled key={message.id} gap="10px" justify="start" $stackItem>
                    <InfoOutlined />
                    <div>
                      <Typography size="xs" color="primaryText">
                        {formatBannerDateTime(message)}
                      </Typography>
                      <Typography size="md">
                        <Linkify messageText={message.message ?? ''} linkClass="custom-link" />
                      </Typography>
                    </div>
                  </MessageBubbleStyled>
                )
              )
            ) : (
              <MessageBubbleStyled gap="10px" justify="start">
                <InfoOutlined />
                <Typography size="md"> </Typography>
              </MessageBubbleStyled>
            )}
          </MessagesStackStyled>
        )}
      </BannerContentWrapStyled>
    </InfoCard>
  );

  if (isMobile) {
    return (
      <BannerCardWrapStyled>
        <Accordion
          expanded={isBannerExpanded}
          onChange={(_e, exp): void => setIsBannerExpanded(exp)}
          disableGutters
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '&.MuiAccordion-root': {
              backgroundColor: (theme): string => theme.palette.background.paper,
              minHeight: 'unset',
              overflow: 'hidden',
            },
            '&.MuiAccordion-root:not(.Mui-expanded)': {
              minHeight: 'unset',
              '& .MuiAccordion-region': { display: 'none' },
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
            sx={{
              minHeight: 40,
              py: 0,
              '& .MuiAccordionSummary-content': { margin: '8px 0', alignItems: 'center' },
              '& .MuiAccordionSummary-expandIconWrapper': {
                transform: 'none',
                '&.Mui-expanded': { transform: 'rotate(180deg)' },
              },
            }}
          >
            <MuiTypography sx={{ fontSize: 15, fontWeight: 500 }}>Messages</MuiTypography>
            {hasMessages && (
              <MuiTypography sx={{ color: 'success.main', fontSize: 15, fontWeight: 500, ml: 1 }}>
                ({sortedMessages.length})
              </MuiTypography>
            )}
          </AccordionSummary>
          <AccordionDetails
            sx={{
              pt: 0,
              pb: 1,
              px: 0,
              overflow: 'auto',
              maxHeight: '50vh',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {bannerInnerContent}
          </AccordionDetails>
        </Accordion>
      </BannerCardWrapStyled>
    );
  }

  return (
    <HorizontallyCollapsibleCard
      title="Messages"
      storageKey={StorageE.BannerCardCollapsed}
      collapsedIcon={<ChatBubbleOutlineIcon />}
      collapsedCount={hasMessages ? sortedMessages.length : undefined}
      disableOnMobile
      isMobile={isMobile}
    >
      <BannerCardWrapStyled>{bannerInnerContent}</BannerCardWrapStyled>
    </HorizontallyCollapsibleCard>
  );
};

export default CardRow;
